import { useEffect, useRef } from 'preact/hooks'
import { i18nChangeLanguage } from 'i18n/i18n'

import { getSingleParamFromUrl } from 'utils/urls'
import { getCurrentLanguage } from 'utils/current-language'
import { localeStringToFloat } from 'utils/format-number'
import { formatCurrencyAmount } from 'app-utils/validate-currency-input'
import { validateDiscountRate } from 'app-utils/validate-discount'

import { CurrencyExchangeDetails, ExchangeDirection } from 'models/exchange-transfer-calculate'
import { CurrencyValueForm } from 'models/apps'

export type InitialExchangeConfig = {
  DEFAULT_EXCHANGE_DATA: CurrencyExchangeDetails
  DEFAULT_CTA_HREF: string
  DEFAULT_LANGUAGE: string
  ALLOWED_WALUTOMAT_HOSTS: string[]
  DEFAULT_DARK_MODE_OPTION: boolean
  DISCOUNT_RATE: number
}

type InitialExchangeParamsHookProps = {
  isSettingsLoading: boolean
  isCurrencyPairSupported: (baseCurrency: string, counterCurrency: string) => boolean
  isCurrencySupported: (currency: string) => boolean
  setDirection: (direction: ExchangeDirection) => void
  setBaseCurrency: (baseCurrency: string) => void
  setCounterCurrency: (counterCurrency: string) => void
  setValue: (value: CurrencyValueForm) => void
  setCtaHref?: (ctaHref: string) => void
  setIsDarkMode?: (isDarkMode: boolean) => void
  setDiscountRate?: (discountRate: number) => void
  config: InitialExchangeConfig
  lang?: string
}

const getCurrencyFromParam = (
  isCurrencySupported: (val: string) => boolean,
  param: string | null,
  fallback: string,
  name: string,
): string => {
  const upperParam = param?.toUpperCase()
  if (upperParam && isCurrencySupported(upperParam)) return upperParam
  if (param) console.warn(`Invalid param or unsupported currency - ${name}: "${param}" — falling back to "${fallback}"`)
  return fallback
}

const getDirectionFromParam = (param: string | null, fallback: ExchangeDirection): ExchangeDirection => {
  if (param === 'buy' || param === 'sell') return param === 'buy' ? 'forward' : 'back'
  if (param) console.warn(`Invalid param - direction: "${param}" — falling back to "${fallback}"`)
  return fallback
}

const getValueFromParam = (param: string | null, fallback: string): CurrencyValueForm => {
  if (!param) return formatCurrencyAmount(fallback)

  const validatedValue = formatCurrencyAmount(param)
  if (validatedValue.isValueCorrect) return validatedValue
  console.warn(`Invalid param - value: "${param}" — falling back to "${JSON.stringify(fallback)}"`)
  return formatCurrencyAmount(fallback)
}

const getCtaHrefFromParam = (param: string | null, fallback: string, allowedHosts: string[]): string => {
  if (!param) return fallback

  const isValidWalutomatUrl = (href: string): boolean => {
    try {
      const url = new URL(href)
      return url.protocol === 'https:' && allowedHosts.includes(url.hostname)
    } catch {
      return false
    }
  }
  if (isValidWalutomatUrl(param)) return param
  console.warn(`Invalid param - ctaHref: "${param}" — falling back to "${fallback}"`)
  return fallback
}

const getIsDarkModeFromParam = (param: string | null, fallback: boolean): boolean => {
  if (param === 'true') return true
  if (param === 'false') return false
  if (param) console.warn(`Invalid param - isDarkMode: "${param}" — falling back to "${fallback}"`)
  return fallback
}

const getDiscountRateFromParam = (param: string | null, fallback: number): number => {
  if (!param) return fallback
  const floatValue = parseFloat(localeStringToFloat(param))
  return validateDiscountRate(floatValue, fallback)
}

export const useInitialExchangeParams = ({
  isSettingsLoading,
  isCurrencyPairSupported,
  isCurrencySupported,
  setDirection,
  setBaseCurrency,
  setCounterCurrency,
  setValue,
  setCtaHref,
  setIsDarkMode,
  setDiscountRate,
  config,
  lang,
}: InitialExchangeParamsHookProps) => {
  const {
    DEFAULT_EXCHANGE_DATA,
    DEFAULT_CTA_HREF,
    DEFAULT_LANGUAGE,
    ALLOWED_WALUTOMAT_HOSTS,
    DEFAULT_DARK_MODE_OPTION,
    DISCOUNT_RATE,
  } = config
  const isInitialized = useRef(false)

  useEffect(() => {
    if (isSettingsLoading || isInitialized.current) return

    const resolvedLang = lang ?? getCurrentLanguage() ?? DEFAULT_LANGUAGE
    i18nChangeLanguage(resolvedLang)

    // Get raw params from URL
    const hrefParam = getSingleParamFromUrl('ctaHref')
    const baseCurrencyParam = getSingleParamFromUrl('baseCurrency')
    const counterCurrencyParam = getSingleParamFromUrl('counterCurrency')
    const directionParam = getSingleParamFromUrl('direction')
    const valueParam = getSingleParamFromUrl('value')
    const isDarkModeParam = getSingleParamFromUrl('isDarkMode')
    const discountRateParam = getSingleParamFromUrl('discountRate')

    // Parse and validate params
    const initialDirection = getDirectionFromParam(directionParam, DEFAULT_EXCHANGE_DATA.direction)
    const initialValue = getValueFromParam(valueParam, DEFAULT_EXCHANGE_DATA.baseAmount)
    const initialBaseCurrency = getCurrencyFromParam(
      isCurrencySupported,
      baseCurrencyParam,
      DEFAULT_EXCHANGE_DATA.baseCurrency,
      'baseCurrency',
    )
    const initialCounterCurrency = getCurrencyFromParam(
      isCurrencySupported,
      counterCurrencyParam,
      DEFAULT_EXCHANGE_DATA.counterCurrency,
      'counterCurrency',
    )
    const initialIsDarkMode = getIsDarkModeFromParam(isDarkModeParam, DEFAULT_DARK_MODE_OPTION)
    const initialCtaHref = getCtaHrefFromParam(hrefParam, DEFAULT_CTA_HREF, ALLOWED_WALUTOMAT_HOSTS)
    const initialDiscountRate = getDiscountRateFromParam(discountRateParam, DISCOUNT_RATE)

    // Set initial states
    setDirection(initialDirection)
    setValue(initialValue)
    if (isCurrencyPairSupported(initialBaseCurrency, initialCounterCurrency)) {
      setBaseCurrency(initialBaseCurrency)
      setCounterCurrency(initialCounterCurrency)
    } else {
      console.warn(
        `The currency pair ${initialBaseCurrency}-${initialCounterCurrency} is not supported. Falling back to defaults ${DEFAULT_EXCHANGE_DATA.baseCurrency}-${DEFAULT_EXCHANGE_DATA.counterCurrency}`,
      )
      setBaseCurrency(DEFAULT_EXCHANGE_DATA.baseCurrency)
      setCounterCurrency(DEFAULT_EXCHANGE_DATA.counterCurrency)
    }
    if (setIsDarkMode) setIsDarkMode(initialIsDarkMode)
    if (setCtaHref) setCtaHref(initialCtaHref)
    if (setDiscountRate) setDiscountRate(initialDiscountRate)

    isInitialized.current = true
  }, [isSettingsLoading])
}
